import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import Dropdown from "react-bootstrap/Dropdown";
import "../List.css";
import down from "../../../../assets/Vehicles/chevron_down.png";
import search from "../../../../assets/Vehicles/search.png";
import ReactPaginate from "react-paginate";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import lar from "../../../../assets/svg/lar.svg";
import rar from "../../../../assets/svg/rar.svg";
import { getCommonTableListApi } from "./table-list-api.js";
import DeleteModal from "../../../common/delete-modal.js";

// --- Constants ---
const PAGE_SIZES = [10, 25, 50, 75, 100];

// Helper styles
const cellStyle = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "150px",
};

const largeCellStyle = {
  ...cellStyle,
  maxWidth: "200px",
};

export const InspectionHistoryTableList = ({ vehicleId }) => {
  // --- State ---
  const [globalFilter, setGlobalFilter] = useState("");
  const [serviceList, setServiceList] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(null);
  
  const [deleteId, setDeleteId] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);

  // --- Callbacks ---
  const handleSnackbarShow = useCallback(() => setShowSnackbar(true), []);
  // eslint-disable-next-line
  const handleSnackbarClose = useCallback(() => setShowSnackbar(false), []);

  const getTableList = useCallback(async () => {
    try {
      const response = await getCommonTableListApi(
        vehicleId,
        "inspection_history_vehicle",
        `search=${globalFilter}&limit=${itemsPerPage}&skip=${itemOffset * itemsPerPage}`
      );
      if (response.data.success === true) {
        setServiceList(response.data.histories);
        setPageCount(response.data.total_pgs);
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching category:", err);
    }
  }, [vehicleId, globalFilter, itemsPerPage, itemOffset]);

  useEffect(() => {
    getTableList();
  }, [getTableList]);

  const handlePageClick = useCallback((event) => {
    setItemOffset(event.selected);
  }, []);

  const cancelDelete = useCallback(() => {
    setDeleteId(null);
    setIsOpenDelete(false);
  }, []);

  const onSuccessDelete = useCallback((response) => {
    setDeleteId(null);
    setSnackbarMessage(response.message);
    setSnackbarStatus(response.success);
    handleSnackbarShow();
    if (response.success === true) {
      setIsOpenDelete(false);
      getTableList();
    }
  }, [getTableList, handleSnackbarShow]);

  // --- Columns ---
  const columns = useMemo(() => [
    {
      header: "Inspection Type",
      accessorKey: "type",
      cell: (info) => (
        <div style={cellStyle}>
          {info.getValue()}
        </div>
      ),
    },
    {
      header: "Vehicle Number",
      accessorKey: "lic_no",
      cell: (info) => (
        <div style={largeCellStyle}>
          {info.getValue()}
        </div>
      ),
    },
    {
      header: "Start Date",
      accessorKey: "start_date",
      cell: (info) => (
        <div style={largeCellStyle}>
          {info.getValue()}
        </div>
      ),
    },
    {
      header: "Start Time",
      accessorKey: "start_time",
      cell: (info) => (
        <div style={cellStyle}>
          {info.getValue()}
        </div>
      ),
    },
    {
      header: "Submitted Date",
      accessorKey: "submitted_date",
      cell: (info) => (
        <div style={largeCellStyle}>
          {info.getValue()}
        </div>
      ),
    },
    {
      header: "Submitted Time",
      accessorKey: "submitted_time",
      cell: (info) => {
        return (
          <div className="hover-action-cell" style={cellStyle}>
            <span>{info.getValue()} </span>
          </div>
        );
      },
    },
  ], []);

  // --- Table Instance ---
  const table = useReactTable({
    data: serviceList ?? [],
    columns,
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <>
      {isOpenDelete && (
        <DeleteModal
          deleteUrl={`inspection/delete/${deleteId}`}
          onSuccess={onSuccessDelete}
          onCancel={cancelDelete}
        />
      )}

      <div className="p-3">
        <div className="d-flex flex-wrap mb-3 align-items-center justify-content-between">
          <p className="m-0">Details table</p>
          <div className="search-wrapper d-flex align-items-center gap-2 bg-white rounded-3 px-3 py-2">
            <img src={search} alt="search" />
            <input
              type="text"
              placeholder="Search"
              className="border-0 outline-0 rounded-3"
              value={globalFilter || ""}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
          </div>
        </div>
        <div className="table-container rounded-4 overflow-x-auto">
          <table>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-3 py-2"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.column.getIsSorted() === "asc" && " asc"}
                      {header.column.getIsSorted() === "desc" && " des"}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-2">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="py-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <Dropdown>
            <span className="pagination-color">Records per page: &nbsp;</span>
            <Dropdown.Toggle
              id="dropdown-basic"
              className="bg-transparent pagination-color border-0 custom-dropdown-toggle p-0"
            >
              <span className="bg-white no-of-rows rounded-3 px-2 py-2">
                {itemsPerPage} <img src={down} alt="" />
              </span>
            </Dropdown.Toggle>
            <Dropdown.Menu
              className="my-2"
              style={{ minWidth: "unset", width: "30%" }}
            >
              {PAGE_SIZES.map((size) => (
                <Dropdown.Item
                  key={size}
                  onClick={() => {
                    table.setPageSize(size);
                    setItemsPerPage(size);
                    setItemOffset(0);
                  }}
                  active={itemsPerPage === size}
                  className="text-center"
                >
                  {size}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <div className="d-flex align-items-center gap-2">
            <ReactPaginate
              previousLabel={<img src={lar} className="ar3" alt="previous" />}
              nextLabel={<img src={rar} className="ar3" alt="next" />}
              breakLabel={"..."}
              pageCount={pageCount + 1}
              marginPagesDisplayed={2}
              pageRangeDisplayed={3}
              onPageChange={handlePageClick}
              containerClassName={"pagination"}
              activeClassName={"active"}
            />
          </div>
        </div>
      </div>
    </>
  );
};
